import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { get, update, isEmpty, cloneDeep, isNil, zip } from 'lodash'
import {
  AutomationTarget,
  Button,
  CardContainer,
  CheckboxField,
  ExpandableCard,
  Form,
  Icon,
  InputField,
  MaskedInputField,
  RadioGroupField,
  SelectField,
  Text,
  constants,
  utils,
  hooks,
  ExpandableCardProps,
} from '@adc/ui-core'
import * as Validators from '../../../../util/validators'
import { TrusteePurchaseProfile, User } from '@adc/sdk/types'
import { DropdownProps } from 'semantic-ui-react'
import * as graph from '@adc/sdk'
import classnames from 'classnames/bind'
import { stateOptions } from '../../entityForm/shared/utils/forms'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const { ColorNames } = constants

const TRUSTEE_PURCHASE_PROFILE_FORM_NAME = 'trustee_purchase_profile'

interface FormFieldConfig extends AutomationTarget {
  name: string
  label: React.ReactNode | string
  type?: 'text' | 'number' | 'email' | 'password' | 'checkbox'
  checked?: boolean
  className?: string
  required?: boolean
  placeholder?: string
  validation?: {
    pattern?: RegExp
    minLength?: number
    maxLength?: number
    min?: number
    max?: number
    custom?: (value: unknown) => string | undefined
    dependsOn?: {
      fields: string[]
      validator: (
        value: unknown,
        dependentValues: unknown[]
      ) => string | undefined
    }
  }
  value?: unknown
  disabled?: boolean
  component: React.ComponentType<any>
  extraProps?: Record<string, unknown>
}

interface FormSectionConfig {
  id: number
  key: string
  title: string
  fields: FormFieldConfig[]
  extraProps?: Record<string, unknown>
}

export interface ComputedTrusteePurchaseProfile extends TrusteePurchaseProfile {
  __computed__: {
    is_refund_address_same_as_deed_mailing_address: boolean
    deed_same_address_as_previous: boolean
    owner_same_address_as_previous: boolean
  }
}

const PURCHASING_FOR_SELF_OPTIONS = [
  {
    label: 'Yes',
    value: graph.enums.TrusteeCustomerType.Owner,
    'data-elm-id': 'purchasing_for_self',
    displayCheck: true,
  },
  {
    label: 'No',
    value: graph.enums.TrusteeCustomerType.Representative,
    'data-elm-id': 'not_purchasing_self',
    displayCheck: true,
  },
]

const generateContactFields = (key: string): FormFieldConfig[] => [
  {
    name: `${key}.company_name`,
    elmId: `${key}.company_name`,
    label: 'Company Name',
    component: InputField,
    required: false,
    validation: {
      dependsOn: {
        fields: [`${key}.first_name`, `${key}.last_name`],
        validator: (value, dependentValues) => {
          if (dependentValues.every(v => !v) && !value) {
            return 'Please input either your Company Name or First & Last Name.'
          }
          return undefined
        },
      },
    },
  },
  {
    name: `${key}.first_name`,
    elmId: `${key}.first_name`,
    label: 'First Name',
    component: InputField,
    required: false,
    validation: {
      dependsOn: {
        fields: [`${key}.company_name`],
        validator: (value, dependentValues) => {
          if (!dependentValues[0] && !value) {
            return 'Please input either your Company Name or First & Last Name.'
          }
          return undefined
        },
      },
    },
  },
  {
    name: `${key}.last_name`,
    elmId: `${key}.last_name`,
    label: 'Last Name',
    component: InputField,
    required: false,
    validation: {
      dependsOn: {
        fields: [`${key}.company_name`],
        validator: (value, dependentValues) => {
          if (!dependentValues[0] && !value) {
            return 'Please input either your Company Name or First & Last Name.'
          }
          return undefined
        },
      },
    },
  },
]

const generateAddressFields = (key: string): FormFieldConfig[] => [
  {
    name: `${key}.street_address`,
    elmId: `${key}.street_address`,
    label: 'Address',
    component: InputField,
    required: true,
  },
  {
    name: `${key}.municipality`,
    elmId: `${key}.municipality`,
    label: 'City',
    className: 'row',
    component: InputField,
    required: true,
  },
  {
    name: `${key}.country_primary_subdivision`,
    elmId: `${key}.country_primary_subdivision`,
    label: 'State',
    className: 'row',
    component: SelectField,
    required: true,
    placeholder: 'Select',
    extraProps: {
      options: stateOptions('owner_section'),
    },
  },
  {
    name: `${key}.postal_code`,
    elmId: `${key}.postal_code`,
    label: 'Zip Code',
    className: 'clear-fix',
    component: MaskedInputField,
    required: true,
    extraProps: {
      mask: utils.inputMasks.zipcodeMask,
    },
    validation: {
      pattern: constants.regex.ZIPCODE_SYNTAX,
    },
  },
]

// FormSectionConfig Inspired from resi-fe-agent-dashboard
// https://github.com/auctioncorp/resi-fe-agent-dashboard/blob/main/src/common/components/landing/agentNetwork.tsx#L72 - waitlistFormFields

const profileInfoSection: FormSectionConfig = {
  id: 1,
  key: 'profile_section',
  title: 'Profile Information',
  fields: [
    {
      name: 'profile_name',
      elmId: 'profile_name',
      label: 'Profile Name',
      component: InputField,
      required: true,
      validation: {
        minLength: 2,
        maxLength: 100,
      },
    },
    {
      name: 'customer_type',
      elmId: 'customer_type',
      label: 'Are you purchasing for yourself?',
      component: RadioGroupField,
      required: true,
      extraProps: {
        options: PURCHASING_FOR_SELF_OPTIONS,
      },
    },
  ],
  extraProps: {
    heightHints: {
      collapsed: 72,
      expanded: 355,
    },
  },
}

const ownerInfoSection: FormSectionConfig = {
  id: 2,
  key: 'owner_section',
  title: 'Owner Information',
  fields: [
    ...generateContactFields('contact_info'),
    {
      name: '__computed__.owner_same_address_as_previous',
      elmId: 'owner_same_address_as_previous',
      label: (
        <Text color='gray' size='tiny'>
          {`Same address from previous page`}
        </Text>
      ),
      component: CheckboxField,
      type: 'checkbox',
      extraProps: {
        checked: false,
      },
    },
    ...generateAddressFields('contact_info.contact_address'),
    {
      name: 'contact_info.phone',
      elmId: 'contact_info.phone',
      label: 'Phone Number',
      component: MaskedInputField,
      required: true,
      extraProps: {
        mask: utils.inputMasks.phoneNumberMask,
        inputLabel: '+1',
      },
      validation: {
        pattern: Validators.PHONE_REGEX,
        custom: value =>
          Validators.phonePatternValidator({}, value)
            ? undefined
            : Validators.PHONE_ERROR,
      },
    },
    {
      name: 'contact_info.email',
      elmId: 'contact_info.email',
      label: 'Email Address',
      component: InputField,
      required: true,
      validation: {
        pattern: constants.regex.EMAIL_SYNTAX,
      },
    },
  ],
  extraProps: {
    heightHints: {
      collapsed: 72,
      expanded: 1012,
    },
  },
}

const deedMailingInfoSection: FormSectionConfig = {
  id: 3,
  key: 'deed_mailing_section',
  title: 'Deed Mailing Address',
  fields: [
    ...generateContactFields('deed_mailing_address'),
    {
      name: '__computed__.deed_same_address_as_previous',
      elmId: 'deed_same_address_as_previous',
      label: (
        <Text color='gray' size='tiny'>
          {`Same address from previous page`}
        </Text>
      ),
      component: CheckboxField,
      type: 'checkbox',
      extraProps: {
        checked: false,
      },
    },
    ...generateAddressFields('deed_mailing_address'),
    {
      name: 'deed_mailing_address.phone',
      elmId: 'deed_mailing_address.phone',
      label: 'Phone Number',
      component: MaskedInputField,
      required: true,
      extraProps: {
        mask: utils.inputMasks.phoneNumberMask,
        inputLabel: '+1',
      },
      validation: {
        pattern: Validators.PHONE_REGEX,
        custom: value =>
          Validators.phonePatternValidator({}, value)
            ? undefined
            : Validators.PHONE_ERROR,
      },
    },
  ],
  extraProps: {
    heightHints: {
      collapsed: 72,
      expanded: 910,
    },
  },
}

const refundMailingInfoSection: FormSectionConfig = {
  id: 4,
  key: 'refund_mailing_section',
  title: 'Refund Payable To',
  fields: [
    {
      name: '__computed__.is_refund_address_same_as_deed_mailing_address',
      elmId: 'is_refund_address_same_as_deed_mailing_address',
      label: (
        <Text color='gray' size='tiny'>
          {`Same as Deed Mailing Address`}
        </Text>
      ),
      type: 'checkbox',
      component: CheckboxField,
      extraProps: {
        checked: false,
      },
    },
    ...generateContactFields('refund_payable_to_address'),
    ...generateAddressFields('refund_payable_to_address'),
    {
      name: 'refund_payable_to_address.phone',
      elmId: 'refund_payable_to_address.phone',
      label: 'Phone Number',
      component: MaskedInputField,
      required: true,
      extraProps: {
        mask: utils.inputMasks.phoneNumberMask,
        inputLabel: '+1',
      },
      validation: {
        pattern: Validators.PHONE_REGEX,
        custom: value =>
          Validators.phonePatternValidator({}, value)
            ? undefined
            : Validators.PHONE_ERROR,
      },
    },
  ],
  extraProps: {
    heightHints: {
      collapsed: 72,
      expanded: 910,
    },
  },
}

const vestingInfoSection: FormSectionConfig = {
  id: 5,
  key: 'vesting_info_section',
  title: 'Vesting Details',
  fields: [
    {
      name: 'vesting_instructions',
      elmId: 'vesting_instructions',
      label: 'How should the purchase be legally vested?',
      component: InputField,
      required: true,
    },
  ],
  extraProps: {
    heightHints: {
      collapsed: 72,
      expanded: 170,
    },
  },
}

const getBaseSlotStructure = (isMobile: boolean) => {
  const size = isMobile ? 'none' : 'medium'
  return {
    card: ({
      children,
      elmId = 'Card',
    }: { children?: React.ReactNode; elmId?: string } = {}) => (
      <CardContainer
        elmId={elmId}
        borderRadius={size}
        padding={size}
        dropShadow={size}
      >
        {children}
      </CardContainer>
    ),
    header: null,
    content: null,
  }
}

const getAccordionIconProps = (valid: boolean) => {
  return valid
    ? { name: 'circle-check', color: ColorNames.AdcLightGreen }
    : { name: 'circle-minus', color: ColorNames.GrayMediumLight }
}

const getCardHeaders = (title: string, isValid: boolean) => {
  return (
    <div data-elm-id={`profile-header`} style={{ width: '100%' }}>
      <Icon
        {...getAccordionIconProps(isValid)}
        iconStyle='solid'
        size='medium'
      />

      <Text
        as='span'
        className={cx('accordion-title-text', 'u-ml-1')}
        size='large'
        weight='heavy'
      >
        {title}
      </Text>
    </div>
  )
}

const getDefaultValues = () => ({
  customer_type: graph.enums.TrusteeCustomerType.Owner,
  is_refund_address_different: true,
  is_default: false,
  is_max_bid_submit: false,
  contact_info: {
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    contact_address: {
      street_address: '',
      municipality: '',
      country_primary_subdivision: '',
      postal_code: '',
    },
  },
  deed_mailing_address: {
    company_name: '',
    first_name: '',
    last_name: '',
    phone: '',
    street_address: '',
    municipality: '',
    country_primary_subdivision: '',
    postal_code: '',
  },
  refund_payable_to_address: {
    company_name: '',
    first_name: '',
    last_name: '',
    phone: '',
    street_address: '',
    municipality: '',
    country_primary_subdivision: '',
    postal_code: '',
  },
  __computed__: {
    is_refund_address_same_as_deed_mailing_address: false,
    owner_same_address_as_previous: false,
    deed_same_address_as_previous: false,
  },
})

type TrusteePurchaseProfileFormProps = {
  purchaseProfiles?: Array<TrusteePurchaseProfile>
  selectedProfile: TrusteePurchaseProfile | null
  user?: User
  onFormStateChange?: (v: { isValid: boolean; isDirty: boolean }) => void
  onSubmit?: (
    values: ComputedTrusteePurchaseProfile
  ) => Promise<{ success: boolean; message?: string }>
}

type TrusteePurchaseProfileFormRefProps = {
  submit: (
    e?: React.FormEvent<HTMLFormElement>
  ) => Promise<{ success: boolean; message?: string }>
}

const TrusteePurchaseProfileForm = forwardRef<
  TrusteePurchaseProfileFormRefProps,
  TrusteePurchaseProfileFormProps
>(
  (
    {
      purchaseProfiles = [],
      selectedProfile = null,
      onSubmit,
      onFormStateChange,
      user,
    },
    ref
  ) => {
    const initialValues = useMemo(() => {
      if (selectedProfile) {
        return {
          ...cloneDeep(selectedProfile),
          ...(!selectedProfile.is_refund_address_different
            ? {
                refund_payable_to_address: cloneDeep(
                  selectedProfile.deed_mailing_address
                ),
              }
            : {}),
          __computed__: {
            is_refund_address_same_as_deed_mailing_address:
              !selectedProfile.is_refund_address_different,
            owner_same_address_as_previous: false,
            deed_same_address_as_previous: false,
          },
        }
      }
      return {
        ...getDefaultValues(),
        is_default: purchaseProfiles.length === 0,
      }
    }, [selectedProfile, purchaseProfiles]) as ComputedTrusteePurchaseProfile

    const [values, setValues] = useState<ComputedTrusteePurchaseProfile>({
      ...cloneDeep(initialValues),
    })
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [touched, setTouched] = useState<Record<string, boolean>>({})
    const [activeIndex, setActiveIndex] = useState(-1)
    const [formSectionStatus, setFormSectionStatus] = useState<
      Record<string, boolean>
    >({})
    const isMobile = hooks.useBreakpoints() === 'mobile'

    const allSections = useMemo(() => {
      return [
        profileInfoSection,
        ownerInfoSection,
        deedMailingInfoSection,
        refundMailingInfoSection,
        vestingInfoSection,
      ].map(section => {
        let heightHints = section.extraProps
          ?.heightHints as ExpandableCardProps['heightHints']

        if (heightHints) {
          heightHints = {
            ...heightHints,
            ...(isMobile && { collapsed: 24 }),
          }
        }

        return {
          ...section,
          extraProps: {
            ...section.extraProps,
            ...(heightHints && { heightHints }),
          },
        }
      })
    }, [isMobile])

    const isSectionTouched = useCallback(
      (sectionKey: string, currentFields?: Set<string>) => {
        return allSections
          .find(section => section.key === sectionKey)
          ?.fields.some(
            field => touched[field.name] || currentFields?.has(field.name)
          )
      },
      [allSections, touched]
    )

    const isSectionInvalid = useCallback(
      (sectionKey: string, errors: Record<string, string>) => {
        return allSections
          .find(section => section.key === sectionKey)
          ?.fields.some(field => errors[field.name])
      },
      [allSections]
    )

    const isValid = useMemo(() => {
      return isEmpty(errors)
    }, [errors])

    const isDirty = useMemo(() => {
      return !isEmpty(touched)
    }, [touched])

    useEffect(() => {
      const formState = {
        isValid,
        isDirty,
      }

      onFormStateChange && onFormStateChange(formState)
    }, [isValid, isDirty, onFormStateChange])

    const fieldSectionMap = useMemo(() => {
      const map: Record<string, string> = {}
      allSections.forEach(section => {
        section.fields.forEach(field => {
          map[field.name] = section.key
        })
      })
      return map
    }, [allSections])

    const allFields = useMemo(() => {
      return [
        ...profileInfoSection.fields,
        ...ownerInfoSection.fields,
        ...deedMailingInfoSection.fields,
        ...refundMailingInfoSection.fields,
        ...vestingInfoSection.fields,
      ]
    }, [
      profileInfoSection,
      ownerInfoSection,
      deedMailingInfoSection,
      refundMailingInfoSection,
      vestingInfoSection,
    ])

    const updateAddressFromUserInfo = useCallback(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (updatedValues: any, key: string) => {
        if (isEmpty(user)) return updatedValues

        let address = {} as Record<string, string>

        const addresses = user.party?.addresses || []

        if (addresses.length > 0 && addresses[0]) {
          const streetNumber = addresses[0].street_number
          const streetName = addresses[0].street_name

          address.street_address = `${streetNumber} ${streetName}`
          address.municipality = addresses[0].municipality || ''
          address.country_primary_subdivision =
            addresses[0].country_primary_subdivision || ''
          address.postal_code = addresses[0].postal_code || ''
        }

        update(updatedValues, `${key}`, () => ({
          ...updatedValues[key],
          ...address,
        }))

        return updatedValues
      },
      [user]
    )

    const validateFields = useCallback(
      (
        fieldsToValidate: Set<string>,
        values: ComputedTrusteePurchaseProfile,
        updatedErrors?: Record<string, string>
      ): Record<string, string> => {
        const fields = allFields.filter(f => fieldsToValidate.has(f.name))
        const newErrors = {
          ...errors,
          ...(!isEmpty(updatedErrors) && updatedErrors),
        }

        fields.forEach(field => {
          const value = get(values, field.name, '')

          // Required field validation
          if (field.required && (isNil(value) || value === '')) {
            newErrors[field.name] = `This field is required`
            return
          }

          // Type-specific validations
          if (field.validation) {
            const { pattern, minLength, maxLength, custom, dependsOn } =
              field.validation
            const stringValue = String(value)

            if (pattern && !pattern.test(stringValue)) {
              newErrors[field.name] = `${field.label} format is invalid`
              return
            }

            if (minLength && stringValue.length < minLength) {
              newErrors[
                field.name
              ] = `${field.label} must be at least ${minLength} characters`
              return
            }

            if (maxLength && stringValue.length > maxLength) {
              newErrors[
                field.name
              ] = `${field.label} must be no more than ${maxLength} characters`
              return
            }

            if (custom) {
              const customError = custom(value)
              if (customError) {
                newErrors[field.name] = customError
                return
              }
            }

            if (dependsOn) {
              const dependentFieldNames = dependsOn.fields
              const dependentValues = dependentFieldNames.map(name =>
                get(values, name, '')
              )
              const customError = dependsOn.validator(value, dependentValues)
              if (customError) {
                newErrors[field.name] = customError
                return
              }
            }
          }

          // Remove field from errors if it passes validation
          delete newErrors[field.name]
        })

        setErrors(newErrors)

        const sectionContainsErrors = Object.keys(newErrors).reduce(
          (acc, fieldName) => {
            const sectionKey = fieldSectionMap[fieldName]
            if (sectionKey && !acc.includes(sectionKey)) {
              acc.push(sectionKey)
            }
            return acc
          },
          [] as string[]
        )

        const sectionThatAreTouched = Object.keys(formSectionStatus).filter(
          sectionKey => isSectionTouched(sectionKey, fieldsToValidate)
        )

        setFormSectionStatus(prev => {
          const updatedStatus = { ...prev }
          sectionThatAreTouched.forEach(k => {
            updatedStatus[k] = !sectionContainsErrors.includes(k)
          })
          return updatedStatus
        })

        return newErrors
      },
      [formSectionStatus, fieldSectionMap, errors]
    )

    const validateSection = useCallback(
      (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        values: any,
        section: FormSectionConfig,
        updatedErrors: Record<string, string> = {}
      ) => {
        const fields = section.fields
        const fieldsToValidate = new Set(fields.map(f => f.name))
        return validateFields(fieldsToValidate, values, updatedErrors)
      },
      [validateFields]
    )

    const handleFieldChange = useCallback(
      (fieldName: string) =>
        (e: React.ChangeEvent<HTMLInputElement>, data: DropdownProps) => {
          const sectionKey = fieldSectionMap[fieldName]
          const currentField = allSections
            .find(s => s.key === sectionKey)
            ?.fields.find(f => f.name === fieldName)

          if (!currentField) {
            console.error(`Field not found: ${fieldName}`)
            return
          }

          let newValues = { ...values }

          if (currentField?.type === 'checkbox') {
            // Handle checkbox-specific logic

            update(newValues, fieldName, () => data.checked)

            if (
              fieldName.includes(
                'is_refund_address_same_as_deed_mailing_address'
              )
            ) {
              update(newValues, 'refund_payable_to_address', () => ({
                ...get(
                  data.checked ? values : initialValues,
                  'deed_mailing_address',
                  {}
                ),
              }))
              update(
                newValues,
                'is_refund_address_different',
                () => !data.checked
              )
            } else {
              const key = fieldName.includes('owner')
                ? 'contact_info.contact_address'
                : 'deed_mailing_address'

              if (data.checked) {
                newValues = updateAddressFromUserInfo(newValues, key)
              } else {
                update(newValues, key, () => ({ ...get(initialValues, key) }))
              }
            }

            setValues(newValues)
            validateSection(
              newValues,
              allSections.find(s => s.key === sectionKey)!
            )
            return
          }

          const value = data ? data.value : e.target.value
          update(newValues, fieldName, () => value)
          setValues(newValues)
          setTouched(prev => ({ ...prev, [fieldName]: true }))

          // Validate affected fields (from previous implementation)
          const fieldsToRevalidate = new Set([fieldName])

          if (currentField?.validation?.dependsOn) {
            const dependentFieldNames = currentField.validation.dependsOn.fields
            dependentFieldNames.forEach(name => fieldsToRevalidate.add(name))
          }

          validateFields(fieldsToRevalidate, newValues)
        },
      [values, errors]
    )

    const handleSubmit = useCallback(
      async (e?: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault()
        const fieldsToValidate = new Set(allFields.map(f => f.name))
        const errors = validateFields(fieldsToValidate, values)

        if (!isEmpty(errors)) {
          return { success: false }
        }

        // Submit the form
        if (onSubmit) return await onSubmit(values)
        return { success: true }
      },
      [values, onSubmit]
    )

    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
    }))

    const isPurchasingForSelf = useMemo(() => {
      return values.customer_type === graph.enums.TrusteeCustomerType.Owner
    }, [values.customer_type])

    useEffect(() => {
      const valuesToUpdate = { ...values }
      if (isPurchasingForSelf) {
        update(valuesToUpdate, 'contact_info', () => {
          return get(initialValues, 'contact_info', {})
        })
      } else {
        update(valuesToUpdate, 'contact_info', () => ({
          company_name: '',
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          contact_address: {
            country_primary_subdivision: '',
            municipality: '',
            postal_code: '',
            street_address: '',
          },
        }))
      }
      update(
        valuesToUpdate,
        '__computed__.owner_same_address_as_previous',
        () => false
      )
      update(
        valuesToUpdate,
        '__computed__.deed_same_address_as_previous',
        () => false
      )
      setValues(valuesToUpdate)
    }, [isPurchasingForSelf])

    useEffect(() => {
      const sectionStatus: Record<string, boolean> = {
        ...Object.fromEntries(allSections.map(section => [section.key, false])),
      }
      let errors = {}
      allSections.forEach(section => {
        errors = validateSection(initialValues, section, errors)
        sectionStatus[section.key] = isEmpty(errors)
      })

      setFormSectionStatus(sectionStatus)
    }, [initialValues, allSections])

    const handleClick = (index: number | string) => {
      if (typeof index !== 'number') return
      setActiveIndex(activeIndex === index ? 0 : index)
    }

    const handleSaveAndContinue = useCallback(
      (section: FormSectionConfig) => (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const errors = validateSection(values, section)
        if (isSectionInvalid(section.key, errors)) return

        const nextSectionId = section.id + 1
        setActiveIndex(nextSectionId)

        // Scroll to next section after state updates
        setTimeout(() => {
          const nextSectionElem = document.querySelector(
            `[data-elm-id='click-area-${nextSectionId}']`
          )
          if (nextSectionElem) {
            const rect = nextSectionElem.getBoundingClientRect()
            const scrollTop = window.pageYOffset + rect.top - 50
            window.scrollTo({ top: scrollTop, behavior: 'smooth' })
          }
        }, 200)
      },
      [validateSection, values, isSectionInvalid]
    )

    const profileSlot = useMemo(() => {
      return {
        ...getBaseSlotStructure(isMobile),
        header: () =>
          getCardHeaders(
            profileInfoSection.title,
            formSectionStatus[profileInfoSection.key]
          ),
        content: () => (
          <div data-elm-id={`profile-content`} className={cx('section')}>
            {profileInfoSection.fields.map(field => {
              const Component = field.component

              return (
                <div className={cx('field-wrapper', `${field?.className}`)}>
                  <Component
                    key={field.name}
                    name={field.name}
                    elmId={field.elmId}
                    placeholder={field.placeholder}
                    label={field.label}
                    onChange={handleFieldChange(field.name)}
                    touched={touched[field.name] || false}
                    error={errors[field.name]}
                    value={get(values, field.name, '')}
                    {...field.extraProps}
                  />
                </div>
              )
            })}
            <div className={cx('continue-btn')}>
              <Button
                disabled={!formSectionStatus[profileInfoSection.key]}
                onClick={handleSaveAndContinue(profileInfoSection)}
              >
                Continue
              </Button>
            </div>
          </div>
        ),
      }
    }, [values, errors, formSectionStatus, isMobile])

    const ownerSlot = useMemo(() => {
      return {
        ...getBaseSlotStructure(isMobile),
        header: () =>
          getCardHeaders(
            ownerInfoSection.title,
            formSectionStatus[ownerInfoSection.key]
          ),
        content: () => (
          <div data-elm-id={`owner-content`} className={cx('section')}>
            {ownerInfoSection.fields.map(field => {
              const Component = field.component

              if (field.type === 'checkbox') {
                field.checked = get(values, field.name, false)
              }

              return (
                <div className={cx('field-wrapper', `${field?.className}`)}>
                  <Component
                    key={field.name}
                    name={field.name}
                    elmId={field.elmId}
                    placeholder={field.placeholder}
                    label={field.label}
                    onChange={handleFieldChange(field.name)}
                    touched={touched[field.name] || false}
                    error={errors[field.name]}
                    value={get(values, field.name, '')}
                    {...field.extraProps}
                    {...(field.type === 'checkbox'
                      ? { checked: field.checked }
                      : {})}
                  />
                </div>
              )
            })}
            <div className={cx('continue-btn')}>
              <Button
                disabled={!formSectionStatus[ownerInfoSection.key]}
                onClick={handleSaveAndContinue(ownerInfoSection)}
              >
                Continue
              </Button>
            </div>
          </div>
        ),
      }
    }, [values, errors, formSectionStatus, isMobile])

    const deedSlot = useMemo(() => {
      return {
        ...getBaseSlotStructure(isMobile),
        header: () =>
          getCardHeaders(
            deedMailingInfoSection.title,
            formSectionStatus[deedMailingInfoSection.key]
          ),
        content: () => (
          <div data-elm-id={`deed-mailing-content`} className={cx('section')}>
            {deedMailingInfoSection.fields.map(field => {
              const Component = field.component

              if (field.type === 'checkbox') {
                field.checked = get(values, field.name, false)
              }

              return (
                <div className={cx('field-wrapper', `${field?.className}`)}>
                  <Component
                    key={field.name}
                    name={field.name}
                    elmId={field.elmId}
                    placeholder={field.placeholder}
                    label={field.label}
                    onChange={handleFieldChange(field.name)}
                    touched={touched[field.name] || false}
                    error={errors[field.name]}
                    value={get(values, field.name, '')}
                    {...field.extraProps}
                    {...(field.type === 'checkbox'
                      ? { checked: field.checked }
                      : {})}
                  />
                </div>
              )
            })}
            <div className={cx('continue-btn')}>
              <Button
                disabled={!formSectionStatus[deedMailingInfoSection.key]}
                onClick={handleSaveAndContinue(deedMailingInfoSection)}
              >
                Continue
              </Button>
            </div>
          </div>
        ),
      }
    }, [values, errors, formSectionStatus, isMobile])

    const refundSlot = useMemo(() => {
      return {
        ...getBaseSlotStructure(isMobile),
        header: () =>
          getCardHeaders(
            refundMailingInfoSection.title,
            formSectionStatus[refundMailingInfoSection.key]
          ),
        content: () => (
          <div data-elm-id={`refund-content`} className={cx('section')}>
            {refundMailingInfoSection.fields.map(field => {
              const Component = field.component

              if (field.type === 'checkbox') {
                field.checked = get(values, field.name, false)
              }

              return (
                <div className={cx('field-wrapper', `${field?.className}`)}>
                  <Component
                    key={field.name}
                    name={field.name}
                    elmId={field.elmId}
                    placeholder={field.placeholder}
                    label={field.label}
                    onChange={handleFieldChange(field.name)}
                    touched={touched[field.name] || false}
                    error={errors[field.name]}
                    value={get(values, field.name, '')}
                    {...field.extraProps}
                    {...(field.type === 'checkbox'
                      ? { checked: field.checked }
                      : {})}
                  />
                </div>
              )
            })}
            <div className={cx('continue-btn')}>
              <Button
                disabled={!formSectionStatus[refundMailingInfoSection.key]}
                onClick={handleSaveAndContinue(refundMailingInfoSection)}
              >
                Continue
              </Button>
            </div>
          </div>
        ),
      }
    }, [values, errors, formSectionStatus, isMobile])

    const vestingSlot = useMemo(() => {
      return {
        ...getBaseSlotStructure(isMobile),
        header: () =>
          getCardHeaders(
            vestingInfoSection.title,
            formSectionStatus[vestingInfoSection.key]
          ),
        content: () => (
          <div data-elm-id={`vesting-content`} className={cx('section')}>
            {vestingInfoSection.fields.map(field => {
              const Component = field.component

              return (
                <div className={cx('field-wrapper', `${field?.className}`)}>
                  <Component
                    key={field.name}
                    name={field.name}
                    elmId={field.elmId}
                    placeholder={field.placeholder}
                    label={field.label}
                    onChange={handleFieldChange(field.name)}
                    touched={touched[field.name] || false}
                    error={errors[field.name]}
                    value={get(values, field.name, '')}
                    {...field.extraProps}
                  />
                </div>
              )
            })}
          </div>
        ),
      }
    }, [values, errors, formSectionStatus, isMobile])

    const zippedSections = useMemo(() => {
      return zip(allSections, [
        profileSlot,
        ownerSlot,
        deedSlot,
        refundSlot,
        vestingSlot,
      ])
    }, [allSections, profileSlot, ownerSlot, deedSlot, refundSlot, vestingSlot])

    return (
      <div className={cx('ui', 'form', 'form-container')}>
        <Form id={TRUSTEE_PURCHASE_PROFILE_FORM_NAME} onSubmit={handleSubmit}>
          {zippedSections.map(
            ([section, slot]) =>
              section &&
              slot && (
                <ExpandableCard
                  key={section.key}
                  id={section.id}
                  slots={slot}
                  active={activeIndex === section.id}
                  onClick={handleClick}
                  bottomMargin={isMobile ? 30 : 24}
                  {...section.extraProps}
                />
              )
          )}
        </Form>
      </div>
    )
  }
)

export default TrusteePurchaseProfileForm
